/**
   * Logo Slider Component
   * Auto-scrolling logo carousel with progressive enhancement
   *
   * Dawn Best Practices 2025:
   * - Progressive Enhancement (works without JS)
   * - Performance-optimized with setInterval pattern
   * - Respects prefers-reduced-motion
   * - Smart pause on user interaction
   *
   * Patch 2026-05-18: Slide-Visibility-Tracking auf IntersectionObserver
   * umgestellt. Vorher scroll-event-getriebenes Read/Write-Pattern über alle
   * Slides => ~700 ms Forced Reflow lt. PageSpeed Insights. CSS reagiert
   * nicht auf aria-hidden/tabindex, daher rein passive Aktualisierung
   * funktional gleichwertig.
   */

  if (!customElements.get('logo-slider')) {
    class LogoSlider extends HTMLElement {
      constructor() {
        super();

        // Settings from data attributes
        this.autoplay = this.dataset.autoplay === 'true';
        this.speed = parseInt(this.dataset.speed) || 3000;
        this.logosPerRow = parseInt(this.dataset.logosPerRow) || 5;
        this.slider = this.querySelector('.logo-slider__track');
        this.slides = this.querySelectorAll('.logo-slider__item');

        // State
        this.autoplayInterval = null;
        this.userInteracting = false;
        this.visibilityObserver = null;

        // Check if reduced motion is preferred (Accessibility)
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Calculate slider item offset (like Dawn's SliderComponent)
        // This is the distance between slides - used for scroll-position based navigation
        this.sliderItemOffset = 0;
      }

      connectedCallback() {
        // Properties for infinite loop
        this.cloneCount = 0;
        this.originalSlidesCount = this.querySelectorAll('.logo-slider__item').length;
        this.loopWidth = 0;

        // Create clones for infinite loop
        if (this.autoplay && !this.prefersReducedMotion && this.slides.length > 1) {
          this.createClones();
        }

        // Initialize slider item offset
        this.initSlides();

        // Setup ResizeObserver to recalculate on window resize
        const resizeObserver = new ResizeObserver(() => this.initSlides());
        resizeObserver.observe(this.slider);

        // Slide-Sichtbarkeit passiv per IntersectionObserver verwalten
        // (ersetzt den scroll-getriebenen Read/Write-Pattern, der ~700 ms
        //  Forced Reflow erzeugt hat — siehe PSI Mobile 2026-05-18)
        this.setupVisibilityObserver();

        // Don't initialize autoplay if:
        // - Disabled in settings
        // - User prefers reduced motion
        // - Only one slide
        if (!this.autoplay || this.prefersReducedMotion || this.slides.length <= 1) {
          return;
        }

        this.init();
      }

      setupVisibilityObserver() {
        // Fallback: Browser ohne IntersectionObserver behalten die statischen
        // aria-Werte aus dem Markup. Slider bleibt funktional, nur dynamische
        // Screen-Reader-Updates entfallen (< 1 % Browser-Anteil weltweit).
        if (!('IntersectionObserver' in window)) return;

        this.visibilityObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const slide = entry.target;
            const links = slide.querySelectorAll('a');
            const isVisible = entry.isIntersecting;

            slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

            if (isVisible) {
              slide.removeAttribute('tabindex');
              links.forEach((link) => link.removeAttribute('tabindex'));
            } else {
              slide.setAttribute('tabindex', '-1');
              links.forEach((link) => link.setAttribute('tabindex', '-1'));
            }
          });
        }, {
          root: this.slider,
          threshold: 0.1
        });

        this.slides.forEach((slide) => this.visibilityObserver.observe(slide));
      }

      createClones() {
        const originalSlides = Array.from(this.slides);

        // Immer genug Clones – egal ob mobile oder desktop
        const cloneCount = Math.min(this.logosPerRow, originalSlides.length);

        this.originalSlidesCount = originalSlides.length;
        this.cloneCount = cloneCount;

        // Letzte N Slides vorne anhängen (left clones)
        for (let i = originalSlides.length - cloneCount; i < originalSlides.length; i++) {
          const clone = originalSlides[i].cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          this.slider.prepend(clone);
        }

        // Erste N Slides hinten anhängen (right clones)
        for (let i = 0; i < cloneCount; i++) {
          const clone = originalSlides[i].cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          this.slider.append(clone);
        }

        // Liste aktualisieren
        this.slides = this.querySelectorAll('.logo-slider__item');

        // Start bei erstem Original (direkt nach den left clones)
        setTimeout(() => {
          this.slider.scrollLeft = originalSlides[0].offsetLeft;
        }, 0);
      }

      initSlides() {
        if (this.slides.length >= 2) {
          this.sliderItemOffset = Math.abs(
            this.slides[1].offsetLeft - this.slides[0].offsetLeft
          );
        } else {
          this.sliderItemOffset = this.slider.offsetWidth;
        }

        if (this.originalSlidesCount) {
          this.loopWidth = this.sliderItemOffset * this.originalSlidesCount;
        }
      }

      init() {
        // Setup user interaction listeners (pause on touch/mouse)
        this.setupInteractionListeners();

        // Setup keyboard focus listeners (accessibility)
        this.setupFocusListeners();

        // Setup jump detection
        this.setupJumpDetection();

        // Start autoplay
        this.startAutoplay();
      }

      setupJumpDetection() {
        if ('onscrollend' in window || 'onscrollend' in this.slider) {
          this.slider.addEventListener('scrollend', () => this.checkJump());
        } else {
          let scrollTimeout;
          this.slider.addEventListener(
            'scroll',
            () => {
              clearTimeout(scrollTimeout);
              scrollTimeout = setTimeout(() => this.checkJump(), 120);
            },
            { passive: true }
          );
        }
      }

      checkJump() {
        if (!this.loopWidth || !this.sliderItemOffset || !this.cloneCount || !this.originalSlidesCount) {
          return;
        }

        const pos = this.slider.scrollLeft;
        const tolerance = 20;

        // erste Original-Position nach den left clones
        const firstOriginalPos = this.sliderItemOffset * this.cloneCount;

        // Start des rechten Clone-Bereichs
        const rightCloneStart =
          this.sliderItemOffset * (this.cloneCount + this.originalSlidesCount);

        // Rechts in den rechten Clones gelandet → eine Original-Breite zurückspringen
        if (pos >= rightCloneStart - tolerance) {
          this.slider.style.scrollBehavior = 'auto';
          this.slider.scrollLeft = pos - this.loopWidth;
          this.slider.style.scrollBehavior = 'smooth';
        }
        // Links in den linken Clones gelandet → eine Original-Breite nach vorne
        else if (pos <= firstOriginalPos - tolerance) {
          this.slider.style.scrollBehavior = 'auto';
          this.slider.scrollLeft = pos + this.loopWidth;
          this.slider.style.scrollBehavior = 'smooth';
        }
      }

      setupInteractionListeners() {
        // Pause autoplay when user starts interacting
        this.slider.addEventListener('touchstart', this.onUserInteractionStart.bind(this), { passive: true });
        this.slider.addEventListener('mousedown', this.onUserInteractionStart.bind(this));

        // Resume autoplay after user stops interacting (with delay)
        this.slider.addEventListener('touchend', this.onUserInteractionEnd.bind(this));
        this.slider.addEventListener('mouseup', this.onUserInteractionEnd.bind(this));
      }

      setupFocusListeners() {
        // Keyboard focus handling (Accessibility requirement from accessibility.md)
        // Pause autoplay when user focuses any element inside slider
        this.addEventListener('focusin', this.onFocusIn.bind(this));
        this.addEventListener('focusout', this.onFocusOut.bind(this));
      }

      onUserInteractionStart() {
        this.userInteracting = true;
        this.pauseAutoplay();
      }

      onUserInteractionEnd() {
        this.userInteracting = false;
        // Resume autoplay after 2 seconds of no interaction
        setTimeout(() => {
          if (!this.userInteracting) {
            this.startAutoplay();
          }
        }, 2000);
      }

      onFocusIn() {
        // Pause autoplay when keyboard user focuses on slider
        this.pauseAutoplay();
      }

      onFocusOut(event) {
        // Resume autoplay when focus leaves slider (after delay)
        setTimeout(() => {
          // Check if focus is still within the slider
          if (!this.contains(document.activeElement)) {
            this.startAutoplay();
          }
        }, 2000);
      }

      startAutoplay() {
        if (this.autoplayInterval) {
          return; // Already running
        }

        // Accessibility: Don't announce slides during autoplay
        this.slider.setAttribute('aria-live', 'off');

        this.autoplayInterval = setInterval(() => {
          this.scrollToNext();
        }, this.speed);
      }

      pauseAutoplay() {
        if (this.autoplayInterval) {
          clearInterval(this.autoplayInterval);
          this.autoplayInterval = null;
        }

        // Accessibility: Announce slides when paused
        this.slider.setAttribute('aria-live', 'polite');
      }

      scrollToNext() {
        // Simply scroll forward - clones handle the infinite loop
        this.slider.scrollTo({
          left: this.slider.scrollLeft + this.sliderItemOffset,
          behavior: 'smooth'
        });
      }

      disconnectedCallback() {
        // Cleanup: Clear interval when element is removed
        this.pauseAutoplay();

        // Cleanup: IntersectionObserver disconnecten
        if (this.visibilityObserver) {
          this.visibilityObserver.disconnect();
          this.visibilityObserver = null;
        }
      }
    }

    customElements.define('logo-slider', LogoSlider);
  }

  // =============================================================================
  // Theme Editor Integration
  // Must be outside customElements check to always listen for events
  // =============================================================================

  // Pause autoplay and scroll to block when selected in editor
  document.addEventListener('shopify:block:select', function(event) {
    const logoSliderBlock = event.target.closest('.logo-slider__item');
    if (!logoSliderBlock) return;

    const parentLogoSlider = event.target.closest('logo-slider');
    if (!parentLogoSlider) return;

    // Pause autoplay while merchant edits
    parentLogoSlider.pauseAutoplay();

    // Scroll to selected block after a short delay (DOM update)
    setTimeout(function() {
      const scrollLeft = logoSliderBlock.offsetLeft -
        (parentLogoSlider.slider.offsetWidth / 2) +
        (logoSliderBlock.offsetWidth / 2);

      parentLogoSlider.slider.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }, 200);
  });

  // Resume autoplay when block is deselected (if it was enabled)
  document.addEventListener('shopify:block:deselect', function(event) {
    const logoSliderBlock = event.target.closest('.logo-slider__item');
    if (!logoSliderBlock) return;

    const parentLogoSlider = event.target.closest('logo-slider');
    if (!parentLogoSlider) return;

    // Only resume if autoplay was enabled in settings
    if (parentLogoSlider.autoplay && !parentLogoSlider.prefersReducedMotion) {
      parentLogoSlider.startAutoplay();
    }
  });

  // Re-initialize when section is loaded/re-rendered
  document.addEventListener('shopify:section:load', function(event) {
    const logoSlider = event.target.querySelector('logo-slider');
    if (!logoSlider) return;

    // Re-init to pick up any setting changes
    if (logoSlider.autoplay && !logoSlider.prefersReducedMotion && logoSlider.slides.length > 1) {
      logoSlider.init();
    }
  });

  // Cleanup when section is unloaded
  document.addEventListener('shopify:section:unload', function(event) {
    const logoSlider = event.target.querySelector('logo-slider');
    if (!logoSlider) return;

    // Pause autoplay to clear interval
    logoSlider.pauseAutoplay();
  });